/**
 * React bindings: `useT(ns)` returns a translate function for the current
 * locale and re-renders the component when the language changes;
 * `useLocale()` just the locale; `<Rich>` interpolates React nodes
 * (a link or a <b> inside a sentence) without splitting the sentence into
 * separately translated fragments.
 */
import { Fragment, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import type { Locale } from './config';
import { getLocale, subscribeLocale } from './store';
import { lookup, makeT, pickPlural, type TFunction } from './translate';
import type { ArgsOf, Messages, Namespace } from './types';

export function useLocale(): Locale {
  return useSyncExternalStore(subscribeLocale, getLocale, getLocale);
}

export function useT<M extends Messages>(ns: Namespace<M>): TFunction<M> {
  const locale = useLocale();
  return useMemo(() => makeT(ns, locale), [ns, locale]);
}

const TOKEN = /\{(\w+)\}/g;

/**
 * Render a message whose placeholders are React nodes:
 *
 *   // en: hint: 'Time zone lives in {personal}.'
 *   <Rich ns={account} k="hint" values={{ personal: <button …>{t('personal')}</button> }} />
 */
export function Rich<M extends Messages, K extends keyof M & string>({
  ns,
  k,
  values,
}: {
  ns: Namespace<M>;
  k: K;
  values: ArgsOf<M[K], ReactNode>[0];
}): ReactNode {
  const locale = useLocale();
  const v = lookup(ns, k, locale);
  const params = (values ?? {}) as Readonly<Record<string, ReactNode>>;
  const text =
    typeof v === 'string' ? v : pickPlural(v, typeof params.count === 'number' ? params.count : 0, locale);
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push(text.slice(last, idx));
    const name = m[1] as string;
    parts.push(<Fragment key={`${name}-${idx}`}>{name in params ? params[name] : m[0]}</Fragment>);
    last = idx + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export type { TFunction };
